import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';

import { ShopScreen } from 'features/shop-base/ShopScreen';
import { withAuth } from 'features/auth/components/withAuth';
import { MainLayout } from '../components/MainLayout';
import {
    getDetailsAndExclusionsFilter,
    ProductDetailTerms,
} from 'features/product/ProductDetailTerms';
import Skeleton from 'react-loading-skeleton';
import { getWalletCardById, getRemoteCardDetails } from 'common/api/wallet/cards';
import { useApi } from 'common/modules/async-actions/useApi';
import { createStyles } from 'utils/createStyle';
import { getBrand, getProductsByBrand } from 'common/api/e-comm/brands';
import { ProductAmountInput } from 'features/product/components/ProductAmountInput';
import { ReloadableProductAmountFormData } from 'features/product/components/ReloadTabPanel';
import { sortOptionValuesAscending } from 'features/product/components/utils';
import { Btn } from 'components/btn/Btn';
import { FormBtn } from 'components/form-btn';
import { useAddProductToCart } from 'hooks/useAddProductToCart';
import { Product, ProductType } from 'common/api/e-comm/models/Product';
import { Routes } from 'routes';
import { Card, EGiftCard } from 'common/api/e-comm/models/Card';
import { useCardBalance } from 'common/hooks/useCardBalance';
import { CardBalance, CardBalanceErrorType } from '../components/CardBalance';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { copyTextToClipboard } from 'utils';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ArchiveCardModal from '../components/ArchiveCardModal';
import EditCardModal from '../components/EditCardModal';
import { LinkBtn } from 'components/link-btn';
import { MobileBarCodeViewer } from './BarcodeViewer';
import { RemoteCardDetails } from 'common/api/e-comm/models/RemoteCardDetails';
import { useRootSelector } from 'common/features/featuresReducer';
import { StatusCode } from 'common/api/config';
import { useAlert } from 'modules/alerts';
import { waitPromise } from 'utils/promiseHelpers';
import {
    selectWalletAffiliateBonuses,
    viewCardThunk,
} from 'common/features/store/duck/wallet/duck';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserId } from 'common/features/store/duck/account/duck';
import { useThirdPartyViewerUrl } from 'common/hooks/useThirdPartyViewerUrl';
import { ShopOnlineBanner } from '../components/ShopOnlineBanner';
import { useRefreshWalletPolling } from './MyCards';

const styles = createStyles({
    termsSkeleton: ['w-full mt-4', { height: '11rem', minWidth: '100%' }],
    title: 'w-full block text-3xl text-brand-dark font-bold mb-5',
    informations: ['flex justify-between w-full items-center gap-2 lg:gap-8 pb-4'],
    brandImageContainer: [
        'hidden sm:flex w-40 items-center justify-center \
        rounded-xl overflow-hidden p-2 border border-grey-5',
        {
            minWidth: '160px',
            minHeight: '120px',
        },
    ],
    brandImageContainerMobile: [
        'flex sm:hidden w-40 flex items-center justify-center \
        rounded-md overflow-hidden p-1 border border-grey-5',
        {
            width: '60px',
            height: '60px',
        },
    ],
    giftCardDetails: ['w-full sm:w-extra-small rounded-xl p-2 bg-white border border-dark-blue'],
    cardDetailContainer: [
        'font-semibold text-xs leading-4 block text-center h-fit rounded-xl py-4 sm:py-2 p-2 bg-pale-blue border border-brand w-full sm:w-extra-small',
    ],
    addMoneyToThisCard: 'text-xl text-brand-dark font-bold mb-5',
    separator: ['w-full border-t mt-5 sm:mt-8 mb-4', { borderColor: '#C4C4C4' }],
});

const canAddProductToCart = (response?: Response | null) => {
    return !(StatusCode.Gone === response?.status || StatusCode.NotFound === response?.status);
};

const getProduct = (products: Product[], card: Card) => {
    const isPhysicalCard = card?.type === 'GiftCard';

    // get the reloadable product for physical card.
    if (isPhysicalCard) {
        return products.find((product) => product.type === 'Reload');
    }

    // get the product that matches the card type
    return products.find((p) => card?.type && p.type === card?.type);
};

export const MyCardDetailsContent = () => {
    useScrollToTop();

    const addMoneyContainer = useRef<HTMLDivElement>(null);
    const { cardId } = useParams<{ cardId: string }>();
    const [showEditModal, setShowEditModal] = useState(false);
    const [loadCardById, , loadCardByIdResponse] = useApi(getWalletCardById);
    const [loadBrand, loadBrandAsyncState, loadBrandResponse] = useApi(getBrand);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [remoteCardDetails, setRemoteCardDetails] = useState<
        RemoteCardDetails | null | undefined
    >(null);
    const [loadProductsByBrand, loadProductsByBrandAsyncState, loadProductsByBrandResponse] =
        useApi(getProductsByBrand);
    const alert = useAlert();
    const dispatch = useDispatch<any>();
    const userId = useSelector(selectUserId);

    const card = useMemo(() => loadCardByIdResponse?.data, [loadCardByIdResponse]);
    const brand = useMemo(() => loadBrandResponse?.data || undefined, [loadBrandResponse]);
    const product = useMemo(
        () => (card ? getProduct(loadProductsByBrandResponse?.data || [], card) : null),
        [loadProductsByBrandResponse, card]
    );
    const affiliateBrandOffersSelector = useRootSelector(selectWalletAffiliateBonuses);

    useRefreshWalletPolling();

    const affiliateOffers = useMemo(() => {
        if (card?.brandId && affiliateBrandOffersSelector) {
            return affiliateBrandOffersSelector(`${card.brandId}`);
        }
    }, [card?.brandId, affiliateBrandOffersSelector]);

    const shouldHideCardInfo = useMemo(() => !!card?.isThirdPartyViewerFlow, [card]);

    const responseThirdPartyViewerUrl = useThirdPartyViewerUrl({
        cardId: card?.id ?? null,
        shouldHideCardInfo: shouldHideCardInfo,
    });

    const defaultOrg = useRootSelector((s) => {
        const homeDetails = s.store.home.homeDetails;

        if (homeDetails?.organizations && homeDetails?.defaultOrganization) {
            return homeDetails?.organizations[homeDetails?.defaultOrganization?.id];
        }
    });

    const canCheckout = Boolean(defaultOrg?.isActive && defaultOrg?.organization?.isActive);

    const { addProductToCart } = useAddProductToCart();
    const [addingToCart, setAddingToCart] = useState(false);

    const {
        isLoading: isCardBalanceLoading,
        data: cardBalanceData,
        error: cardBalanceError,
    } = useCardBalance(card);

    const cardBalance = useMemo(() => {
        if (card && card.settings?.canCheckBalance) {
            return cardBalanceData?.cardBalance || 0;
        }
        return card?.cardBalance || 0;
    }, [cardBalanceData, card]);

    useEffect(() => {
        if (cardId) {
            loadCardById(cardId).finally(() => {
                setPageLoading(false);
            });
        }
    }, [cardId, loadCardById]);

    useEffect(() => {
        if (userId && cardId) {
            viewCardThunk(userId, cardId)(dispatch);
        }
    }, [userId, cardId, dispatch]);

    useEffect(() => {
        if (card?.settings?.useRemoteCardStorage) {
            const fetchRemoteCard = async () => {
                try {
                    const { data } = await getRemoteCardDetails(card.id);
                    setRemoteCardDetails(data);
                } catch (e) {
                    alert({ message: 'Unable to fetch card details.' });
                }
            };
            fetchRemoteCard();
        }
    }, [card, alert]);

    useEffect(() => {
        if (card && !loadProductsByBrandResponse?.data) {
            loadBrand(card.brandId);
            loadProductsByBrand(card.brandId);
        }
    }, [card, loadBrand, loadProductsByBrand, loadProductsByBrandResponse]);

    const initialValues: ReloadableProductAmountFormData = {
        cardId: card?.id || '',
        selectedOptions: product
            ? Object.fromEntries(
                  product.options.map((opt) => [
                      opt.id,
                      sortOptionValuesAscending(opt.values)[0].value,
                  ])
              )
            : {},
        priceOverride: product?.minValue || 0,
        quantity: 1,
    };

    const onEditModalClose = () => {
        setShowEditModal(false);

        if (cardId) {
            loadCardById(cardId);
        }
    };

    const onSubmit = useCallback(
        async (
            data: ReloadableProductAmountFormData,
            formik: FormikHelpers<ReloadableProductAmountFormData>
        ) => {
            const isValid = await formik.validateForm(data);
            if (isValid && product) {
                let canAddToCart = ProductType.EGiftCard === product.type;

                // CASE: The card is type Reload -- we should check if the card is still `active`
                // block from adding to cart if it is __not__ active.
                if (product?.type === ProductType.Reload) {
                    canAddToCart = canAddProductToCart(cardBalanceError?.response);
                }

                if (canAddToCart) {
                    waitPromise(addProductToCart(product, data, brand), setAddingToCart);
                } else {
                    // CASE: Product type is Reload and their card is not active
                    alert({
                        title: 'Inactive Reload',
                        message: 'This reload card is inactive and cannot be added to cart.',
                        showCloseButton: true,
                    });
                }
            }
        },
        [product, addProductToCart, brand, alert, cardBalanceError]
    );

    const isLoading = useMemo(
        () => pageLoading || loadBrandAsyncState.loading || loadProductsByBrandAsyncState.loading,
        [pageLoading, loadBrandAsyncState, loadProductsByBrandAsyncState]
    );

    const amountInputLabel = useMemo(() => {
        if (product?.isReloadable) {
            return 'Reload amount';
        }
        if (card?.type === ProductType.GiftCard) {
            return 'Card Amount';
        }
        if (card?.type === ProductType.EGiftCard) {
            return 'eGift Value';
        }
        return '';
    }, [card, product]);

    const scrollToAddMoney = () => {
        if (!addMoneyContainer || !addMoneyContainer.current) {
            return;
        }

        const targetY = addMoneyContainer.current.offsetTop;
        window.scrollTo({
            top: targetY,
            left: 0,
            behavior: 'smooth',
        });
    };

    const [cardBalanceErrorType, setCardBalanceErrorType] = useState<CardBalanceErrorType>();

    return (
        <MainLayout section="detail">
            <EditCardModal
                isOpen={showEditModal}
                closeModal={onEditModalClose}
                card={card as Card}
            />
            <div className="sm:ml-4">
                <div className="flex flex-row justify-between">
                    <h1 className={styles.title}>{card?.name || brand?.name}</h1>
                    <button
                        className={classNames(
                            'pl-4 pr-0 md:p-0 items-start cursor-pointer',
                            product?.isReloadable ? 'flex' : 'hidden'
                        )}
                        onClick={() => setShowEditModal(true)}
                    >
                        <FontAwesomeIcon icon={faEdit} className="text-brand md:hidden" />
                        <small className="hidden md:block text-xs text-brand underline whitespace-nowrap">
                            Edit Card Details
                        </small>
                    </button>
                </div>
                {!isLoading && (
                    <>
                        <div className="flex flex-row">
                            <div className={styles.informations}>
                                <div>
                                    <div>
                                        <div className={styles.brandImageContainer}>
                                            <img src={brand?.imageUrl} />
                                        </div>
                                        <div className={styles.brandImageContainerMobile}>
                                            <img src={brand?.imageUrl} />
                                        </div>
                                    </div>
                                    <div className="hidden sm:flex mt-2">
                                        {card && !shouldHideCardInfo ? (
                                            <CardBalance
                                                card={card}
                                                loadCard={loadCardById}
                                                isLoading={isCardBalanceLoading}
                                                onAddMoneyClick={scrollToAddMoney}
                                                balance={cardBalanceData}
                                                cardBalanceError={cardBalanceError}
                                                shouldHideCardBalance={shouldHideCardInfo}
                                                onEditCardDetails={() => setShowEditModal(true)}
                                                cardBalanceErrorType={cardBalanceErrorType}
                                                setCardBalanceErrorType={setCardBalanceErrorType}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                                {card && !shouldHideCardInfo ? (
                                    <MobileBarCodeViewer
                                        card={card}
                                        brand={brand}
                                        shouldHideCardBarCode={shouldHideCardInfo}
                                    />
                                ) : null}
                            </div>
                            <div className="hidden sm:block ml-auto pt-0">
                                {card && !shouldHideCardInfo ? (
                                    <CardActivationCodes
                                        remoteCardDetails={remoteCardDetails}
                                        card={card}
                                        shouldHideCardInfo={shouldHideCardInfo}
                                    />
                                ) : null}
                                {card && shouldHideCardInfo ? (
                                    <ThirdPartyUrlAction
                                        url={responseThirdPartyViewerUrl.thirdPartyViewerUrl}
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className="block sm:hidden">
                            {card && !shouldHideCardInfo ? (
                                <CardBalance
                                    card={card}
                                    loadCard={loadCardById}
                                    isLoading={isCardBalanceLoading}
                                    onAddMoneyClick={scrollToAddMoney}
                                    balance={cardBalanceData}
                                    cardBalanceError={cardBalanceError}
                                    shouldHideCardBalance={shouldHideCardInfo}
                                    onEditCardDetails={() => setShowEditModal(true)}
                                    cardBalanceErrorType={cardBalanceErrorType}
                                    setCardBalanceErrorType={setCardBalanceErrorType}
                                />
                            ) : null}
                            <div className="mt-2 sm:mt-8">
                                {card && !shouldHideCardInfo ? (
                                    <CardActivationCodes
                                        remoteCardDetails={remoteCardDetails}
                                        card={card}
                                        shouldHideCardInfo={shouldHideCardInfo}
                                    />
                                ) : null}
                                {card && shouldHideCardInfo ? (
                                    <ThirdPartyUrlAction
                                        url={responseThirdPartyViewerUrl.thirdPartyViewerUrl}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </>
                )}
                {isLoading && (
                    <div className="mb-2">
                        <Skeleton height={160} />
                    </div>
                )}
                {affiliateOffers && affiliateOffers.canDoubleDip ? (
                    <ShopOnlineBanner
                        brandId={card!.brandId}
                        affiliateOffer={affiliateOffers}
                        atRaiseRight={true}
                    />
                ) : (
                    <div className={styles.separator}></div>
                )}
                {!isLoading && product && (
                    <div ref={addMoneyContainer}>
                        {product.isReloadable && (
                            <div className={styles.addMoneyToThisCard}>Add money to this card</div>
                        )}
                        {!product.isReloadable && (
                            <div className={styles.addMoneyToThisCard}>Buy again</div>
                        )}
                        <Formik initialValues={initialValues} onSubmit={onSubmit} key={product.id}>
                            {({ isValid }: FormikProps<ReloadableProductAmountFormData>) => (
                                <Form>
                                    <div className="flex">
                                        <div className="w-full">
                                            <ProductAmountInput
                                                product={product}
                                                label={amountInputLabel}
                                                cardBalance={cardBalance}
                                                hideQuantityField={product?.canOverridePrice}
                                            />
                                        </div>
                                        <div className="ml-auto hidden sm:block">
                                            <FormBtn
                                                isSubmitting={addingToCart}
                                                disabled={
                                                    !isValid ||
                                                    !canCheckout ||
                                                    (cardBalanceErrorType &&
                                                        cardBalanceErrorType !== 'GenericApiError')
                                                }
                                                className="truncate ml-2"
                                            >
                                                Add to Cart
                                            </FormBtn>
                                        </div>
                                    </div>
                                    <div className="flex sm:hidden my-4">
                                        <FormBtn
                                            isSubmitting={addingToCart}
                                            disabled={!isValid || !canCheckout}
                                            className="truncate mx-auto"
                                        >
                                            Add to Cart
                                        </FormBtn>
                                    </div>
                                    {product?.isOvernightReload && (
                                        <p className="text-brand font-semibold text-xs text-center sm:text-right mb-4">
                                            Funds will be available the next business day.
                                        </p>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    </div>
                )}
                {isLoading && (
                    <div className="mb-2">
                        <Skeleton height={65} />
                    </div>
                )}
                {card ? (
                    <div className="w-full border-t-2 border-brand">
                        <ProductDetailTerms
                            brandId={card.brandId}
                            productType={card.type}
                            defaultExpandedFilter={getDetailsAndExclusionsFilter}
                            showAffiliateTerms={Boolean(affiliateOffers)}
                        />
                    </div>
                ) : null}
                {!isLoading && (
                    <div className="mb-2">
                        <div className="flex flex-col justify-center text-center my-16">
                            <div>
                                {card ? (
                                    <Btn
                                        isSecondary={true}
                                        onClick={() => setShowArchiveModal(true)}
                                    >
                                        {`${
                                            card.type === ProductType.GiftCard
                                                ? 'Remove'
                                                : 'Archive'
                                        } This Card`}
                                    </Btn>
                                ) : null}
                            </div>
                            <div className="mt-5">
                                <NavLink className="text-xs font-bold" to={Routes.WalletMyCards}>
                                    Go Back to My Cards
                                </NavLink>
                            </div>
                        </div>
                    </div>
                )}
                {isLoading && (
                    <div className="mt-16">
                        <Skeleton height={65} />
                    </div>
                )}
            </div>
            {card ? (
                <ArchiveCardModal
                    isOpen={showArchiveModal}
                    closeModal={() => setShowArchiveModal(false)}
                    cardId={card?.id}
                    cardType={card.type}
                />
            ) : null}
        </MainLayout>
    );
};

const CardActivationCodes = ({
    card,
    remoteCardDetails,
    shouldHideCardInfo,
}: {
    card: Card;
    remoteCardDetails: RemoteCardDetails | null | undefined;
    shouldHideCardInfo: boolean;
}): JSX.Element => {
    const [isNumberCopied, setIsNumberCopied] = useState<boolean>(false);
    const [isPinCopied, setIsPinCopied] = useState<boolean>(false);
    const [isExtraFieldCopied, setIsExtraFieldCopied] = useState<boolean>(false);
    const { cardNumber: remoteCardNumber = '', pin: remotePin = '' } = remoteCardDetails || {};
    const cardNumber =
        (card?.settings?.useRemoteCardStorage ? remoteCardNumber : card.cardNumber) || '';
    const cardPin = (card?.settings?.useRemoteCardStorage ? remotePin : card.pin) || '';

    enum RestrictionTypeTexts {
        PrintedCardRequired = 'This card may only be redeemed as a physical printout.',
        PinRequired = 'This retailer requires the PIN when making a purchase.',
        PhysicalCardRequired = 'This retailer requires you to use your physical gift card for in-store purchases.',
        HomeDepotDelayed = '',
        Unknown = '',
    }

    const onCopyNumber = useCallback(() => {
        if (cardNumber) {
            copyTextToClipboard(cardNumber);
            setIsNumberCopied(true);
            setIsPinCopied(false);
            setIsExtraFieldCopied(false);

            setTimeout(() => {
                setIsNumberCopied(false);
            }, 3000);
        }
    }, [cardNumber]);

    const onCopyPin = useCallback(() => {
        const pinToCopy = cardPin || '0000';
        copyTextToClipboard(pinToCopy);
        setIsPinCopied(true);
        setIsNumberCopied(false);
        setIsExtraFieldCopied(false);

        setTimeout(() => {
            setIsPinCopied(false);
        }, 3000);
    }, [cardPin]);

    const onCopyExtraField = useCallback(() => {
        const value = card.extraFieldValue || '0000';
        copyTextToClipboard(value);
        setIsExtraFieldCopied(true);
        setIsNumberCopied(false);
        setIsPinCopied(false);

        setTimeout(() => {
            setIsExtraFieldCopied(false);
        }, 3000);
    }, [card.extraFieldValue]);

    const renderPinCopyInfo = (pin: string | null) => {
        if (pin) {
            return isPinCopied ? (
                <FontAwesomeIcon icon={faCheckCircle} className="ml-auto text-success" size="lg" />
            ) : (
                <LinkBtn
                    style={{ marginLeft: 'auto', fontWeight: 'bold', textAlign: 'right' }}
                    onClick={onCopyPin}
                >
                    Copy PIN
                </LinkBtn>
            );
        }
    };

    const renderExtraFieldCopyInfo = (value: string | null, label: string | null) => {
        if (value) {
            return isExtraFieldCopied ? (
                <FontAwesomeIcon icon={faCheckCircle} className="ml-auto text-success" size="lg" />
            ) : (
                <LinkBtn className="ml-auto font-bold text-right " onClick={onCopyExtraField}>
                    Copy {label}
                </LinkBtn>
            );
        }
    };

    if (shouldHideCardInfo) {
        return <></>;
    }

    return (
        <div className="w-full mt-8 sm:m-0 mx-auto sm:max-w-xs text-xs sm:ml-auto">
            {card.settings?.restrictionType &&
            RestrictionTypeTexts[card.settings?.restrictionType] !== '' ? (
                <p className="flex text-brand font-semibold pb-2">
                    {RestrictionTypeTexts[card.settings?.restrictionType]}
                </p>
            ) : null}
            <div className={styles.giftCardDetails}>
                <div className="text-brand-dark font-bold">
                    {card?.settings?.cardNumber?.label || 'Gift Code'}
                </div>

                <div className="flex justify-between">
                    <div data-recording-disable className="text-grey-1 whitespace-wrap">
                        {cardNumber || '0000 0000 0000 0000 0000'}
                    </div>

                    {isNumberCopied ? (
                        <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="ml-auto text-success"
                            size="lg"
                        />
                    ) : (
                        <LinkBtn
                            style={{ marginLeft: 'auto', fontWeight: 'bold', textAlign: 'right' }}
                            onClick={onCopyNumber}
                        >
                            Copy Code
                        </LinkBtn>
                    )}
                </div>

                {((card.settings?.useRemoteCardStorage && remotePin) ||
                    card.settings?.pin?.isVisible) && (
                    <>
                        <div className="text-brand-dark font-bold mt-2">
                            {card?.settings?.pin?.label ?? 'PIN'}
                        </div>
                        <div data-recording-disable className="flex justify-between">
                            <div className="text-grey-1">{cardPin || '----'}</div>
                            {renderPinCopyInfo(cardPin)}
                        </div>
                    </>
                )}

                {card.settings?.extraField?.isVisible && (
                    <>
                        <div className="text-brand-dark font-bold mt-2">
                            {card.settings.extraField.label}
                        </div>
                        <div data-recording-disable className="flex">
                            <div className="text-grey-1">{card.extraFieldValue || '----'}</div>
                            {renderExtraFieldCopyInfo(
                                card.extraFieldValue,
                                card.settings.extraField.label
                            )}
                        </div>
                    </>
                )}

                {card.type === ProductType.EGiftCard && (card as EGiftCard)?.viewerUrl && (
                    <div className="flex justify-center">
                        <a
                            href={(card as EGiftCard).viewerUrl as string}
                            className="mt-6 font-bold text-right"
                            target="_blank"
                        >
                            Generate Print View
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

const ThirdPartyUrlAction = ({ url }: { url?: string }) => {
    return (
        <div className={styles.cardDetailContainer}>
            <a target="_blank" className="block hover:underline w-full sm:w-80" href={url}>
                View Card Details
            </a>
        </div>
    );
};

const MyCardDetails = withAuth()(() => (
    <ShopScreen>
        <MyCardDetailsContent />
    </ShopScreen>
));

export default MyCardDetails;
